import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { MupiItem } from "@/store/mupis/mupiAPI"

export const STEPS = ["mupis", "content", "review", "payment", "confirmation"]
// Types
export type Channel = "MUPI" | "APP"

export type ContentType = "UPLOADED" | "TEMPLATE" | "AI_GENERATED"

export interface ContentDetails {
  id: string
  name: string
  contentUrl: string
  tags: string[]
  channel: Channel
  type: ContentType
  formatId: string
  templateId: string | null
  contentTitle: string | null
  contentText: string | null
  templateFileUrl: string | null
  tenantId: string
  createdBy: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  format: {
    id: string
    name: string
    aspectRatio: string
    layout: string
  }
  template: {
    id: string
    name: string
    previewUrl: string
  } | null
  tenant: {
    id: string
    name: string
  }
  creator: {
    id: string
    name: string
  }
}

export type CampaignStatus = "scheduled" | "ongoing" | "completed"

export interface CampaignContent {
  id: string
  name: string
  type: string
  thumbnail?: string
  appliedToAllMupis: boolean
}

export interface CampaignBooking {
  id: string
  status: "Booking Confirmed" | "Pending" | "Cancelled"
  bookingDate: string
  paymentDate: string
  duration: number
}

export interface CampaignEngagement {
  approximateImpressionsPerDay: number
  totalQrCodeScans: number
  qrCodeEngagementRate: number
}

export interface CampaignPayment {
  basePrice: number
  duration: number
  subtotal: number
  vatRate: number
  vatAmount: number
  grandTotal: number
  invoiceUrl?: string
}

export interface Campaign {
  id: string
  name: string
  status: CampaignStatus
  image?: string
  dates?: string
  mupis?: number
  daysLeft?: number
  description?: string
  createdAt?: string
  updatedAt?: string
  targetAudience?: string
  budget?: number
  booking?: CampaignBooking
  engagement?: CampaignEngagement
  content?: CampaignContent
  mupiLocations?: MupiItem[]
  payment?: CampaignPayment
}

// Campaign Creation State
export interface MupiWithContent {
  mupi: MupiItem
  content: ContentDetails | null
}

export interface CampaignCreationState {
  //  Basic Info
  campaignName: string
  startDate: string | null
  endDate: string | null
  duration: number // in days
  caption: string

  // Step 2: Mupi Selection
  selectedMupis: MupiItem[]
  toggleMupiForContent: MupiItem | null // For selecting/deselecting mupis when assigning content

  // Step 3: Content Assignment
  mupiContentMap: Record<string, ContentDetails[] | null> // mupiId -> content
  globalContent: ContentDetails | null // Content applied to all mupis
  isGlobalContentApplied: boolean

  // Step 4: Review
  shareOnMoogliFeed: boolean

  // Step 4: Payment
  payment: CampaignPayment | null

  // UI State
  currentStep: "mupis" | "content" | "review" | "payment" | "confirmation"
  isLoading: boolean
  error: string | null
}

const initialState: CampaignCreationState = {
  campaignName: "",
  startDate: null,
  endDate: null,
  caption: "",
  duration: 0,
  selectedMupis: [],
  toggleMupiForContent: null,
  mupiContentMap: {},
  globalContent: null,
  isGlobalContentApplied: false,
  shareOnMoogliFeed: false,
  payment: null,
  currentStep: "mupis",
  isLoading: false,
  error: null,
}

const campaignCreationSlice = createSlice({
  name: "campaignCreation",
  initialState,
  reducers: {
    resetState: (state) => (state = initialState),
    // Basic Info Actions
    setCampaignName: (state, action: PayloadAction<string>) => {
      state.campaignName = action.payload
    },
    setCampaignCaption: (state, action: PayloadAction<string>) => {
      state.caption = action.payload
    },

    setDates: (state, action: PayloadAction<{ startDate: string; endDate: string }>) => {
      state.startDate = action.payload.startDate
      state.endDate = action.payload.endDate

      // Calculate duration
      const start = new Date(action.payload.startDate)
      const end = new Date(action.payload.endDate)
      state.duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
    },

    // Mupi Selection Actions
    addMupi: (state, action: PayloadAction<MupiItem>) => {
      const exists = state.selectedMupis.find((m) => m.id === action.payload.id)
      if (!exists) {
        state.selectedMupis.push(action.payload)
        // Initialize content map entry
        state.mupiContentMap[action.payload.id] = null
      }
    },

    // Hydrate the slice with the canonical mupi list from the server cart.
    // Preserves any existing `mupiContentMap` entries that still apply.
    setSelectedMupisFromCart: (state, action: PayloadAction<MupiItem[]>) => {
      state.selectedMupis = action.payload
      const keep: Record<string, ContentDetails[] | null> = {}
      for (const m of action.payload) {
        keep[m.id] = state.mupiContentMap[m.id] ?? null
      }
      state.mupiContentMap = keep
    },

    /** Replace content map entries for payment/review hydration from the cart. */
    setMupiContentMapFromCart: (state, action: PayloadAction<Record<string, ContentDetails[] | null>>) => {
      const next: Record<string, ContentDetails[] | null> = {}
      for (const mupi of state.selectedMupis) {
        next[mupi.id] = action.payload[mupi.id] ?? state.mupiContentMap[mupi.id] ?? null
      }
      state.mupiContentMap = next
    },

    removeMupi: (state, action: PayloadAction<string>) => {
      state.selectedMupis = state.selectedMupis.filter((m) => m.id !== action.payload)
      if (state.selectedMupis.length === 0) {
        state.mupiContentMap = {}
      }
    },

    removeMupiFromContentMap: (state, action: PayloadAction<string>) => {
      delete state.mupiContentMap[action.payload]
    },

    clearAllMupis: (state) => {
      state.selectedMupis = []
      state.mupiContentMap = {}
    },

    toggleMupi: (state, action: PayloadAction<MupiItem>) => {
      state.toggleMupiForContent = action.payload
    },

    // Content Assignment Actions
    setContentForMupi: (state, action: PayloadAction<{ mupiId: string; content: ContentDetails }>) => {
      const currentContents = state.mupiContentMap[action.payload.mupiId]
      if (currentContents == null) {
        state.mupiContentMap[action.payload.mupiId] = [action.payload.content]
      } else {
        const exists = currentContents.some((c) => c.id === action.payload.content.id)
        if (!exists) {
          state.mupiContentMap[action.payload.mupiId] = [...currentContents, action.payload.content]
        }
      }
    },

    removeContentFromMupi: (state, action: PayloadAction<{ mupiId: string; contentId: string }>) => {
      const currentContents = state.mupiContentMap[action.payload.mupiId]
      if (currentContents) {
        state.mupiContentMap[action.payload.mupiId] = currentContents.filter(
          (content) => content.id !== action.payload.contentId,
        )
        // If no contents left, set to null
        if (state.mupiContentMap[action.payload.mupiId]?.length === 0) {
          state.mupiContentMap[action.payload.mupiId] = null
        }
      }
    },

    clearAllContentFromMupi: (state, action: PayloadAction<string>) => {
      state.mupiContentMap[action.payload] = null
    },

    setGlobalContent: (state, action: PayloadAction<ContentDetails>) => {
      state.globalContent = action.payload
      // Apply to all mupis
      state.selectedMupis.forEach((mupi) => {
        const currentContents = state.mupiContentMap[mupi.id]
        if (currentContents === null) {
          state.mupiContentMap[mupi.id] = [{ ...action.payload }]
        } else {
          state.mupiContentMap[mupi.id] = [...currentContents, { ...action.payload }]
        }
      })
    },

    clearGlobalContent: (state) => {
      state.globalContent = null
      // Clear all mupi contents
      state.selectedMupis.forEach((mupi) => {
        state.mupiContentMap[mupi.id] = null
      })
    },

    checkGlobalContentApplied: (state, action: PayloadAction<boolean>) => {
      state.isGlobalContentApplied = action.payload
    },

    // TODO: DELETE IF NOT NEEDED
    assignContentToMultipleMupis: (state, action: PayloadAction<{ mupiIds: string[]; content: ContentDetails }>) => {
      action.payload.mupiIds.forEach((mupiId) => {
        if (mupiId in state.mupiContentMap) {
          const currentContents = state.mupiContentMap[mupiId]
          if (currentContents === null) {
            state.mupiContentMap[mupiId] = [action.payload.content]
          } else {
            const contentExists = currentContents.some((c) => c.id === action.payload.content.id)
            if (!contentExists) {
              state.mupiContentMap[mupiId] = [...currentContents, action.payload.content]
            }
          }
        }
      })
    },

    // Remove content from multiple mupis
    removeContentFromMultipleMupis: (state, action: PayloadAction<{ mupiIds: string[]; contentId: string }>) => {
      action.payload.mupiIds.forEach((mupiId) => {
        if (mupiId in state.mupiContentMap) {
          const currentContents = state.mupiContentMap[mupiId]
          if (currentContents !== null) {
            state.mupiContentMap[mupiId] = currentContents.filter((content) => content.id !== action.payload.contentId)
            if (state.mupiContentMap[mupiId].length === 0) {
              state.mupiContentMap[mupiId] = null
            }
          }
        }
      })
    },
    setShareOnMoogliFeed: (state, action: PayloadAction<boolean>) => {
      state.shareOnMoogliFeed = action.payload
    },

    // Payment Actions
    calculatePayment: (state) => {
      // A screen without a listing price (approved pricing) adds nothing here.
      const basePrice = state.selectedMupis.reduce((sum, mupi) => sum + (mupi.priceNumber ?? 0) * state.duration, 0)
      const vatRate = 0.21 // 21% VAT
      const subtotal = basePrice
      const vatAmount = subtotal * vatRate
      const grandTotal = subtotal + vatAmount

      state.payment = {
        basePrice,
        duration: state.duration,
        subtotal,
        vatRate,
        vatAmount,
        grandTotal,
      }
    },

    setPayment: (state, action: PayloadAction<CampaignPayment>) => {
      state.payment = action.payload
    },

    // Navigation Actions
    setCurrentStep: (state, action: PayloadAction<CampaignCreationState["currentStep"]>) => {
      state.currentStep = action.payload
    },

    goToNextStep: (state) => {
      const steps: CampaignCreationState["currentStep"][] = ["mupis", "content", "review", "payment", "confirmation"]
      const currentIndex = steps.indexOf(state.currentStep)
      if (currentIndex < steps.length - 1) {
        state.currentStep = steps[currentIndex + 1]
      }
    },

    goToPreviousStep: (state) => {
      const steps: CampaignCreationState["currentStep"][] = ["mupis", "content", "review", "payment", "confirmation"]
      const currentIndex = steps.indexOf(state.currentStep)
      if (currentIndex > 0) {
        state.currentStep = steps[currentIndex - 1]
      }
    },

    // Utility Actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    resetCampaignCreation: () => initialState,

    // Update mupi details (e.g., price changes)
    updateMupi: (state, action: PayloadAction<{ mupiId: string; updates: Partial<MupiItem> }>) => {
      const mupi = state.selectedMupis.find((m) => m.id === action.payload.mupiId)
      if (mupi) {
        Object.assign(mupi, action.payload.updates)
      }
    },
  },
})

export const {
  resetState,
  setCampaignName,
  setCampaignCaption,
  setDates,
  addMupi,
  removeMupi,
  setSelectedMupisFromCart,
  setMupiContentMapFromCart,
  clearAllMupis,
  toggleMupi,
  setContentForMupi,
  removeContentFromMupi,
  setGlobalContent,
  clearGlobalContent,
  checkGlobalContentApplied,
  assignContentToMultipleMupis,
  removeContentFromMultipleMupis,
  removeMupiFromContentMap,
  setShareOnMoogliFeed,
  calculatePayment,
  setPayment,
  setCurrentStep,
  goToNextStep,
  goToPreviousStep,
  setLoading,
  setError,
  resetCampaignCreation,
  updateMupi,
} = campaignCreationSlice.actions

export default campaignCreationSlice.reducer

// Selectors
export const selectCampaignName = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.campaignName
export const getCampaignCaption = (state: { campaignCreation: CampaignCreationState }) => state.campaignCreation.caption

export const selectSelectedMupis = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.selectedMupis

export const toggleMupiForContent = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.toggleMupiForContent

export const selectMupiContentMap = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.mupiContentMap

export const selectGlobalContent = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.globalContent
export const isGlobalContentApplied = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.isGlobalContentApplied

export const selectShareOnMoogliFeed = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.shareOnMoogliFeed

export const selectPayment = (state: { campaignCreation: CampaignCreationState }) => state.campaignCreation.payment

export const selectCurrentStep = (state: { campaignCreation: CampaignCreationState }) =>
  state.campaignCreation.currentStep

export const selectDuration = (state: { campaignCreation: CampaignCreationState }) => state.campaignCreation.duration

// Derived selectors
export const selectMupisWithContent = (state: { campaignCreation: CampaignCreationState }): MupiWithContent[] => {
  return state.campaignCreation.selectedMupis.map((mupi) => ({
    mupi,
    content: state.campaignCreation.mupiContentMap[mupi.id]?.[0] || null,
  }))
}

export const selectMupisWithoutContent = (state: { campaignCreation: CampaignCreationState }): MupiItem[] => {
  return state.campaignCreation.selectedMupis.filter((mupi) => !state.campaignCreation.mupiContentMap[mupi.id])
}

export const selectIsReadyForPayment = (state: { campaignCreation: CampaignCreationState }): boolean => {
  const { selectedMupis, mupiContentMap, campaignName, startDate, endDate } = state.campaignCreation

  // Check if all required fields are filled
  if (!campaignName || !startDate || !endDate || selectedMupis.length === 0) {
    return false
  }

  // Check if all mupis have content assigned
  return selectedMupis.every((mupi) => mupiContentMap[mupi.id] !== null)
}

export const selectTotalMupisCount = (state: { campaignCreation: CampaignCreationState }): number =>
  state.campaignCreation.selectedMupis.length

export const selectTotalBasePrice = (state: { campaignCreation: CampaignCreationState }): number => {
  const { selectedMupis, duration } = state.campaignCreation
  return selectedMupis.reduce((sum, mupi) => sum + (mupi.priceNumber ?? 0) * duration, 0)
}
